import React, { useMemo, useState, Fragment } from 'react';

import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import {
  BackgroundType,
  FeaturedImageProps,
} from '@zola/zola-ui/src/components/PreAuthV3/FeaturedImage';
import { MastheadMedia, Values } from '@zola/zola-ui/src/components/PreAuthV3/Masthead';
import {
  MastheadWrapper,
  TitleWrapper,
} from '@zola/zola-ui/src/components/PreAuthV3/Masthead.styles';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';
import { COLORS3, MEDIA_QUERY, SPACING } from '@zola/zola-ui/src/styles/emotion';
import { Title2 } from '@zola/zola-ui/src/typography/Headings';

import styled from '@emotion/styled';
import cx from 'classnames';

import CachedImage from '~/components/common/images/CachedImage';
import VendorsByCategory from '~/components/VendorsByCategory/VendorsByCategory';
import { useAccountIncentivizationContext } from '~/contexts/AccountIncentivizationContext';
import { useUserContext } from '~/contexts/UserContext';
import {
  BookedVendorLandingPagePromoModule,
  BookedVendorsPromoTermsLink,
  SearchBar,
} from '~/pages/couples/explore/components/BookedVendorPromoModule/BookedVendorPromoModule';
import Invitation from '~/pages/couples/explore/components/BookedVendorPromoModule/clara-and-bradley-invitation@2x.png';
import { LANDING_PAGE_VALUES } from '~/pages/couples/explore/components/PreMarketplaceAccountContent/constants';
import EditLocationModal from '~/pages/couples/explore/components/SearchBar/EditLocationModal';
import { getAssetImageUrl } from '~/util/imageUtils';
import { hasMarketplaceAccount } from '~/util/userContextUtils';

import HappyCoupleImage1 from './happy-couple-image-1.jpg';
import HappyCoupleImage2 from './happy-couple-image-2.jpg';
import HeroBackground from './hero-background.jpg';
import SaveTheHand from './save-the-date-hand.jpg';

import styles from './preMarketplaceAccountContent.module.less';

export const MastheadTitle = () => (
  <h1>
    <span>Wedding venues and vendors</span>
  </h1>
);

const NewMasthead = styled(MastheadWrapper)`
  border-bottom: none;
  padding: 0;

  &&& {
    overflow: visible;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  height: 100%;

  ${MEDIA_QUERY.DESKTOP} {
    padding: ${SPACING.LG};
  }

  ${MEDIA_QUERY.DESKTOP_LG} {
    padding: ${SPACING.LG} ${SPACING.XL};
  }

  ul {
    padding-top: ${SPACING.S16};
    padding-bottom: ${SPACING.S32};

    ${MEDIA_QUERY.EXCLUDE_DESKTOP} {
      padding-bottom: 0;
    }
  }
`;

const ImageWrapper = styled.div`
  height: 100%;
  width: 100%;
  padding: ${SPACING.LG} ${SPACING.LG} ${SPACING.LG} 0;

  ${MEDIA_QUERY.EXCLUDE_DESKTOP} {
    display: none;
  }

  picture {
    max-height: 519px;
  }
`;

const Hero = styled.section`
  &&& {
    ${MEDIA_QUERY.MOBILE} {
      padding: ${SPACING.S32} ${SPACING.S24};
    }
    border-bottom: 1px solid ${COLORS3.BLACK_030};
  }
`;

const CategoryAndPromoWrapper = styled.div<{
  isBookedInquiryIncentiveAvailable: boolean;
}>`
  display: flex;
  flex-direction: column;

  ${MEDIA_QUERY.DESKTOP} {
    border-bottom: 1px solid ${COLORS3.BLACK_030};
  }

  ${MEDIA_QUERY.MOBILE} {
    flex-direction: column-reverse;
    ${({ isBookedInquiryIncentiveAvailable }) =>
      !isBookedInquiryIncentiveAvailable && `border-bottom: 1px solid ${COLORS3.BLACK_030};`}
  }

  ${MEDIA_QUERY.TABLET} {
    border-bottom: 1px solid ${COLORS3.BLACK_030};
  }
`;

const QUIZ_PROMO_GAP = `40px`;
const QUIZ_PROMO_PADDING = `40px`;

const QuizPromoModule = styled.section`
  display: flex;
  gap: ${QUIZ_PROMO_GAP};
  &&& {
    padding: ${QUIZ_PROMO_PADDING};
  }
  overflow-x: auto;
  border-bottom: 1px solid ${COLORS3.BLACK_030};

  ${MEDIA_QUERY.MOBILE} {
    display: none;
  }
`;

const Module = styled.div`
  width: 640px;
  height: 312px;
  border-radius: 16px;
  flex-shrink: 0;

  display: flex;
  gap: 0;

  @media (min-width: 1200px) {
    width: calc((100vw - ${QUIZ_PROMO_GAP} - ${QUIZ_PROMO_PADDING} - ${QUIZ_PROMO_PADDING}) / 2);
  }
  @media (min-width: 1920px) {
    width: calc((1920px - ${QUIZ_PROMO_GAP} - ${QUIZ_PROMO_PADDING} - ${QUIZ_PROMO_PADDING}) / 2);
  }
  > div {
    flex-basis: 50%;
    border-radius: 16px;

    &:first-of-type {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    &:last-of-type {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
`;

const QuizCTA = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  background-color: ${COLORS3.BAY_100};
  gap: 16px;
  padding: 40px;

  color: ${COLORS3.SKY_100};
`;

const PromoCTA = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  background-color: ${COLORS3.FOREST_100};
  gap: 16px;
  padding: 40px;

  color: ${COLORS3.MINT_100};

  a {
    color: ${COLORS3.MINT_100};

    &:hover {
      color: ${COLORS3.MINT_100};
      text-decoration: underline;
    }
  }
`;

const HappyCouple = styled.div`
  background-image: url(${HappyCoupleImage1});
  background-size: cover;
  height: 100%;
  width: 100%;
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;

  @container (width > 440px) {
    background-image: url(${HappyCoupleImage2});
  }
`;

const HappyContainer = styled.div`
  container: quiz-cta;
  container-type: inline-size;
`;

const QuizModule = () => {
  return (
    <Module>
      <HappyContainer>
        <HappyCouple />
      </HappyContainer>

      <QuizCTA>
        <Title2 presentation="h3" serif className={styles.sky100}>
          Find the best vendor matches
        </Title2>
        <ButtonV3 component="a" colorway="sky" href="/inspiration/onboard">
          Take our quiz
        </ButtonV3>
      </QuizCTA>
    </Module>
  );
};

const BookedVendorImage = styled.div`
  background-image: url(${Invitation});
  background-size: cover;
`;

const BookedVendorImage2 = styled(CachedImage)``;

const PromoModule = () => {
  return (
    <Module>
      {true ? (
        <div
          style={{
            display: 'flex',
            backgroundColor: '#e8e7e6',
            padding: '16px 16px 0 16px',
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          <BookedVendorImage2
            style={{ width: '100%', height: 'auto', maxHeight: '100%', objectFit: 'contain' }}
            src={SaveTheHand}
            alt=""
            srcSizes={[
              { width: 704, height: 704 },
              { width: 352, height: 352 },
            ]}
            quality={80}
          />
        </div>
      ) : (
        <BookedVendorImage />
      )}
      <PromoCTA>
        <Title2 presentation="h3" serif className={styles.mint100}>
          Book a vendor on Zola and get 65% off save the dates
        </Title2>
        <BookedVendorsPromoTermsLink fontSize={16} />
      </PromoCTA>
    </Module>
  );
};

const PromoModuleWrapper = () => {
  const { isBookedInquiryIncentiveAvailable } = useAccountIncentivizationContext();

  if (!isBookedInquiryIncentiveAvailable()) {
    return <Fragment />;
  }

  return (
    <Fragment>
      <QuizPromoModule>
        <QuizModule />
        <PromoModule />
      </QuizPromoModule>
      <BookedVendorLandingPagePromoModule showSearchBar={true} className={styles.showMobile} />
    </Fragment>
  );
};

const MobileQuizButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;

  ${MEDIA_QUERY.DESKTOP} {
    display: none;
  }
`;

export const VendorsByCategoryContainer = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => {
  return <section className={cx(styles.vendorsByCategory, className)} {...rest} />;
};

export const PreMarketplaceAccountContent = () => {
  const userContext = useUserContext();

  const { isMobile } = useResponsiveDesign();
  const [showLocationModal, setShowLocationModal] = useState(false);
  const { isBookedInquiryIncentiveAvailable } = useAccountIncentivizationContext();

  const mastheadImages: FeaturedImageProps = useMemo(() => {
    return {
      background: {
        type: BackgroundType.IMAGE,
        parameters: {
          featuredSrc: isMobile
            ? getAssetImageUrl(HeroBackground, { width: 700, quality: 80 })
            : getAssetImageUrl(HeroBackground, { width: 1000, quality: 80 }),
          alt: '',
        },
      },
    };
  }, [isMobile]);

  if (hasMarketplaceAccount(userContext)) {
    return null;
  }

  return (
    <Fragment>
      <Hero>
        <NewMasthead>
          <Sidebar>
            <TitleWrapper style={{ padding: 0 }}>
              <MastheadTitle />
            </TitleWrapper>
            <Values valueProps={LANDING_PAGE_VALUES} />
            <SearchBar searchButtonPosition="outside" wrapSearchButton />
            <MobileQuizButtonWrapper>
              <ButtonV3 size="large" component="a" href="/inspiration/onboard">
                Take our quiz
              </ButtonV3>
            </MobileQuizButtonWrapper>
          </Sidebar>
          <ImageWrapper>
            <MastheadMedia images={mastheadImages} />
          </ImageWrapper>
        </NewMasthead>
      </Hero>
      <CategoryAndPromoWrapper
        isBookedInquiryIncentiveAvailable={isBookedInquiryIncentiveAvailable()}
      >
        <PromoModuleWrapper />
        <VendorsByCategoryContainer>
          <VendorsByCategory isClpTest={true} showModal={() => setShowLocationModal(true)} />
        </VendorsByCategoryContainer>
      </CategoryAndPromoWrapper>
      {showLocationModal && <EditLocationModal hideModalFn={() => setShowLocationModal(false)} />}
    </Fragment>
  );
};
